import { Alert, Button, Card, Checkbox, Col, Empty, Row, Select, Space, Spin, Statistic, Table, Tabs, Typography } from 'antd';
import * as React from 'react';
import { CustomerServiceQueries } from '../../queries/customer-service-queries';
import UsageChartCard from './components/UsageChartCard';
import { GabiiCompanyStatistics, GabiiErrorsPerDay, GabiiFeedback, GabiiUsagePerDay } from '../../models/gabii-statistics-interface';
import { Link, useHistory } from 'react-router-dom';
import { SortOrder } from 'antd/lib/table/interface';
import _ from 'lodash';

const { Title } = Typography; 
const { Option } = Select;

export default function GabiiReportPage() {
  const [showDemoCompanies, setShowDemoCompanies] = React.useState<boolean>(false);
  const gabiiDetailsQuery = CustomerServiceQueries.useGabiiReport();
  const history = useHistory();
  const [numberSelection, setNumberSelection] = React.useState<number>(90);
  
  const columns = [
    {
      title: 'Name',
      dataIndex: 'companyName',
      key: 'name',
      sorter: {
        compare: (a: { companyName: string; }, b: { companyName: string; }) => a.companyName.localeCompare(b.companyName)
      },
      render: (companyName: string, statistics: GabiiCompanyStatistics) => {
        return <Link to={`company/${statistics.companyId}`}>{companyName}</Link>
      },
    },
    {
      title: 'Company Id',
      dataIndex: 'companyId',
      key: 'companyId',
      sorter: {
        compare: (a: { companyId: number; }, b: { companyId: number; }) => (a.companyId - b.companyId)
      },
    },
    {
      title: 'User Requests',
      dataIndex: 'userMessagePerDay',
      key: 'userRequests',
      render: (usage: GabiiUsagePerDay[]) => {
        return usage?.filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).map(x => x.userMessageCount)?.reduce((a, b) => a + b, 0);
      },
      sorter: {
        compare: (a: { userMessagePerDay: GabiiUsagePerDay[] }, b: { userMessagePerDay: GabiiUsagePerDay[] }) =>
          (a.userMessagePerDay?.filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).map(x => x.userMessageCount)?.reduce((a, b) => a + b, 0) - b.userMessagePerDay?.filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).map(x => x.userMessageCount)?.reduce((a, b) => a + b, 0))
      },
      defaultSortOrder: 'descend' as SortOrder,
    },
    {
      title: 'Errors',
      dataIndex: 'errorsPerDay',
      key: 'errors',
      render: (errors: GabiiErrorsPerDay[]) => {
        return errors?.filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1)?.map(x => x.errorCount)?.reduce((a, b) => a + b, 0);
      },
      sorter: {
        compare: (a: { errorsPerDay: GabiiErrorsPerDay[] }, b: { errorsPerDay: GabiiErrorsPerDay[] }) =>
          (a.errorsPerDay?.filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).map(x => x.errorCount)?.reduce((a, b) => a + b, 0) - b.errorsPerDay?.filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).map(x => x.errorCount)?.reduce((a, b) => a + b, 0))
      },
    },
    {
      title: 'Positive Feedback',
      dataIndex: 'feedback',
      key: 'pfeedback',
      render: (feedback: GabiiFeedback[]) => {
        return feedback.filter(x => x.positiveFeedback && (Math.abs(new Date(x.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).length
      },
      sorter: {
        compare: (a: { feedback: GabiiFeedback[] }, b: { feedback: GabiiFeedback[] }) =>
          (a.feedback.filter(x => x.positiveFeedback && (Math.abs(new Date(x.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).length - b.feedback.filter(x => x.positiveFeedback && (Math.abs(new Date(x.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).length)
      },
    },
    {
      title: 'Negative Feedback',
      dataIndex: 'feedback',
      key: 'nfeedback',
      render: (feedback: GabiiFeedback[]) => {
        return feedback.filter(x => !x.positiveFeedback && (Math.abs(new Date(x.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).length
      },
      sorter: {
        compare: (a: { feedback: GabiiFeedback[] }, b: { feedback: GabiiFeedback[] }) => 
          (a.feedback.filter(x => !x.positiveFeedback && (Math.abs(new Date(x.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).length - b.feedback.filter(x => !x.positiveFeedback && (Math.abs(new Date(x.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1).length)
      },
    },
  ]

  const AllUsageValues = React.useMemo(() => {
    if(!!gabiiDetailsQuery.data) {
      return (Object.values(_.cloneDeep(gabiiDetailsQuery?.data?.filter(x => showDemoCompanies || !x.demoCompany))?.map(x => x.userMessagePerDay)?.flat()?.reduce((obj, item) => {
        var key = item.date
        if (!obj[key]) {
          obj[key] = Object.assign(item)
        } else {
          obj[key].userMessageCount += item.userMessageCount
        }
        return obj as GabiiUsagePerDay
      }, {})) as GabiiUsagePerDay[]).filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1)
    }

    return [];
  }, [gabiiDetailsQuery.data, showDemoCompanies, numberSelection])

  const AllErrorValues = React.useMemo(() => {
    if(!!gabiiDetailsQuery.data) {
      return (Object.values(_.cloneDeep(gabiiDetailsQuery?.data?.filter(x => showDemoCompanies || !x.demoCompany))?.map(x => x.errorsPerDay)?.flat()?.reduce((obj, item) => {
        var key = item.date
        if (!obj[key]) {
          obj[key] = Object.assign(item)
        } else {
          obj[key].errorCount += item.errorCount
        }
        return obj as GabiiErrorsPerDay
      }, {})) as GabiiErrorsPerDay[]).filter(x => (Math.abs(new Date(x.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1)
    }

    return [];
  }, [gabiiDetailsQuery.data, showDemoCompanies, numberSelection])

  const PositiveFeedbackCount = React.useMemo(() => {
    if(!!gabiiDetailsQuery.data) {
      return gabiiDetailsQuery.data?.filter(x => showDemoCompanies || !x.demoCompany)?.map(x => x.feedback.filter(f => f.positiveFeedback && ((Math.abs(new Date(f.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1)).length)
        ?.flat()?.reduce((partialSum, a) => partialSum + a, 0)
    }

    return 0;
  }, [gabiiDetailsQuery.data, showDemoCompanies, numberSelection])

  const NegativeFeedbackCount = React.useMemo(() => {
    if(!!gabiiDetailsQuery.data) {
      return gabiiDetailsQuery.data?.filter(x => showDemoCompanies || !x.demoCompany)?.map(x => x.feedback.filter(f => !f.positiveFeedback && ((Math.abs(new Date(f.reportDate).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1)).length)
        ?.flat()?.reduce((partialSum, a) => partialSum + a, 0)
    }

    return 0;
  }, [gabiiDetailsQuery.data, showDemoCompanies, numberSelection])

  const ErrorCount = React.useMemo(() => {
    if(!!gabiiDetailsQuery.data) {
      return gabiiDetailsQuery.data?.filter(x => showDemoCompanies || !x.demoCompany)?.map(x => x.errorsPerDay.filter(e =>  ((Math.abs(new Date(e.date).getTime() - (new Date().getTime())) / (24 * 60 * 60 * 1000)) < numberSelection + 1)).map(e => e.errorCount))?.flat()?.reduce((partialSum, a) => partialSum + a, 0)
    }

    return 0;
  }, [gabiiDetailsQuery.data, showDemoCompanies, numberSelection])

  return (
    <Row gutter={[16, 16]}>
      <Col flex={'auto'}>
        <Title level={3}>Gabii Usage Report</Title>
      </Col>
      <Col>
        <Space>
          <Select value={numberSelection} onChange={setNumberSelection} style={{ width: 150 }}>
            <Option key="90" value={90}>Past 90 days</Option>
            <Option key="30" value={30}>Past 30 days</Option>
            <Option key="7" value={7}>Past week</Option>
            <Option key="1" value={1}>Past 24 hours</Option>
          </Select>
          <Checkbox
            checked={showDemoCompanies}
            onClick={() => setShowDemoCompanies(prevState => !prevState)}
          >
            Show Demo Companies
          </Checkbox>
        </Space>
      </Col>
      <Col span={24}>
        <Spin spinning={gabiiDetailsQuery.isLoading}>
          {gabiiDetailsQuery.isError ? (
            <Alert
              type='error'
              message='Unable to retrieve gabii information.'
              showIcon
            />
          ) : (
            <Row gutter={[16, 16]}>
              <Col span={24} style={{ minHeight: 30 }}>
                <UsageChartCard
                  data={AllUsageValues.sort((a, b) => (new Date(b.date).getTime() - new Date(a.date).getTime()))}
                  errorData={AllErrorValues.sort((a, b) => (new Date(b.date).getTime() - new Date(a.date).getTime()))}
                  pageIsLoading={gabiiDetailsQuery.isLoading}
                  chartIsLoading={false}
                />
              </Col>
              <Col span={6}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Card>
                      <Row gutter={[16, 0]}>
                        <Col span={24}>
                          <Title level={5}>Feedback</Title>
                        </Col>
                        <Col span={12}>
                          <Statistic title="Positive" value={PositiveFeedbackCount} valueStyle={{color: "#1D8585"}} />
                        </Col>
                        <Col span={12}>
                          <Statistic title="Negative" value={NegativeFeedbackCount} valueStyle={{color: "#76060C"}} />
                        </Col>
                        <Col span={24}>
                          <Button
                            className='full-width'
                            style={{marginTop: 16}}
                            href={'feedback'}
                            onClick={(e) => {
                              e.preventDefault();
                              history.push('gabii/feedback');
                            }}
                          >
                            View All Feedback
                          </Button>
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  <Col span={24}>
                    <Card>
                      <Row gutter={[16, 0]}>
                        <Col span={24}>
                          <Title level={5}>Errors</Title>
                        </Col>
                        <Col span={12}>
                          <Statistic value={ErrorCount} valueStyle={{color: "#76060C"}} />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              </Col>
              <Col span={18}>
                <Card>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Title level={5}>Company Info</Title>
                    </Col>
                    <Col span={24}>
                      <Table
                        columns={columns}
                        dataSource={gabiiDetailsQuery?.data?.filter(x => showDemoCompanies || !x.demoCompany)}
                        size='middle'
                        locale={{
                          emptyText: (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description={`No users found`}
                            />
                          ),
                        }}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
        </Spin>
      </Col>
    </Row>
  );
}
